import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { useTheme } from '@/client/components/theme-provider'
import { useSession } from '@/client/lib/auth'
import { Menu, Moon, Sun, Zap } from 'lucide-react'
import { siteConfig } from '@/config/site.config'
import { useDynamicHeaderMenu } from '@/client/hooks/useDynamicMenu'
import { MenuRenderer } from '@/client/components/shared/MenuRenderer'
import { HeaderNavSkeleton } from '@/client/components/shared/NavigationSkeleton'

export function Header() {
  const { theme, setTheme } = useTheme()
  const { data: session } = useSession()
  const [mobileOpen, setMobileOpen] = useState(false)
  const { flatItems: navItems, isActive, isLoading } = useDynamicHeaderMenu()

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 font-semibold text-lg">
          <Zap className="h-6 w-6 text-primary" />
          <span>{siteConfig.name}</span>
        </Link>

        {/* Desktop Navigation */}
        {isLoading ? (
          <HeaderNavSkeleton className="hidden md:flex" />
        ) : (
          <MenuRenderer
            items={navItems}
            variant="header"
            isActive={isActive}
            className="hidden md:flex"
          />
        )}

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
            <span className="sr-only">Toggle theme</span>
          </Button>

          {session ? (
            <Button asChild>
              <Link to="/dashboard">Dashboard</Link>
            </Button>
          ) : (
            <>
              <Button variant="ghost" asChild>
                <Link to="/sign-in">Sign In</Link>
              </Button>
              <Button asChild>
                <Link to="/sign-up">Get Started</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu */}
        <div className="flex md:hidden items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px]">
              <div className="mt-8">
                <MenuRenderer
                  items={navItems}
                  variant="mobile"
                  isActive={isActive}
                  onItemClick={() => setMobileOpen(false)}
                />
                <div className="border-t border-border pt-4 mt-4 flex flex-col gap-2">
                  {session ? (
                    <Button asChild className="w-full">
                      <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                        Dashboard
                      </Link>
                    </Button>
                  ) : (
                    <>
                      <Button variant="outline" asChild className="w-full">
                        <Link to="/sign-in" onClick={() => setMobileOpen(false)}>
                          Sign In
                        </Link>
                      </Button>
                      <Button asChild className="w-full">
                        <Link to="/sign-up" onClick={() => setMobileOpen(false)}>
                          Get Started
                        </Link>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
